import React from "react";
import { MotiView, AnimatePresence } from "moti";

import AssignmentPanel from "./AssignmentPanel";
import ModuleDescriptionPanel from "./ModuleDescriptionPanel";
import RecommendationPanel from "./RecommendationPanel";
import SuggestedContentPanel from "./SuggestedContentPanel";
import OverviewPanel from "./OverviewPanel";
import type {
  CourseEvaluatorReviewMockData,
  CourseEvaluatorReviewSidebarItemMock,
} from "../mockData/courseEvaluatorReview.mockData";
import type { ActivityDetailMock } from "../types/reviewTypes";

interface ReviewContentPanelProps {
  review: CourseEvaluatorReviewMockData;
  selectedReviewItem: CourseEvaluatorReviewSidebarItemMock | null;
  activityDetails: Record<string, ActivityDetailMock>;
  onStartReviewing: () => void;
}

function ReviewContentPanel({
  review,
  selectedReviewItem,
  activityDetails,
  onStartReviewing,
}: ReviewContentPanelProps) {
  const itemKey = selectedReviewItem?.id ?? "overview";

  return (
    <AnimatePresence exitBeforeEnter>
      <MotiView
        key={itemKey}
        from={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ type: "timing", duration: 150 }}>
        {selectedReviewItem === null ? (
          <OverviewPanel review={review} onStartReviewing={onStartReviewing} />
        ) : (
          <PanelContent selectedReviewItem={selectedReviewItem} activityDetails={activityDetails} />
        )}
      </MotiView>
    </AnimatePresence>
  );
}

/** Inner component to keep the switch logic clean */
function PanelContent({
  selectedReviewItem,
  activityDetails,
}: {
  selectedReviewItem: CourseEvaluatorReviewSidebarItemMock;
  activityDetails: Record<string, ActivityDetailMock>;
}) {
  const activityDetail = activityDetails[selectedReviewItem.id] ?? null;

  switch (selectedReviewItem.kind) {
    case "moduleDescription":
      return <ModuleDescriptionPanel selectedReviewItem={selectedReviewItem} activityDetail={activityDetail} />;
    case "recommendation":
      return <RecommendationPanel selectedReviewItem={selectedReviewItem} activityDetail={activityDetail} />;
    case "assignment":
      return <AssignmentPanel selectedReviewItem={selectedReviewItem} activityDetail={activityDetail} />;
    case "suggestedContent":
      return <SuggestedContentPanel selectedReviewItem={selectedReviewItem} activityDetail={activityDetail} />;
    default:
      return null;
  }
}

export default ReviewContentPanel;
